import type { DnsechoRow, ShareRow } from '@shared/api'

/**
 * ISO 3166-1 user-assigned code, used when geoip could not place a resolver.
 *
 * The API rejects a share row whose `cc` is not exactly two characters, and
 * ipDetails yields '-' for an unlocatable address -- so passing the geoip value
 * through unchanged makes the whole share fail with retcode -30 because of one
 * unresolvable resolver.
 */
const UNKNOWN_CC = 'XX'
const UNKNOWN = '-'

/**
 * Maps observed resolvers onto the share contract.
 *
 * Every field the API requires must be truthy, so absent geo data is filled
 * rather than omitted. Rows with no query count are dropped: `count` must be
 * truthy, and a zero-count row would fail validation for the whole batch.
 */
export function toShareRows(rows: DnsechoRow[]): ShareRow[] {
    return rows
        .filter((row) => row.count > 0 && row.ip)
        .map((row) => ({
            ip: row.ip,
            cc: row.cc && row.cc.length === 2 ? row.cc : UNKNOWN_CC,
            cityName: row.cityName || UNKNOWN,
            countryName: row.countryName || UNKNOWN,
            location: row.location ?? { latitude: 0, longitude: 0 },
            postal: row.postal || UNKNOWN,
            region: row.region || UNKNOWN,
            count: row.count,
            isp: row.isp,
            asnOrg: row.asnOrg,
        }))
}

/** Share records come back as ShareRow; the results UI speaks DnsechoRow. */
export function fromShareRows(rows: ShareRow[]): DnsechoRow[] {
    return rows.map((row, i) => ({
        ip: row.ip,
        order: i,
        count: row.count,
        cc: row.cc === UNKNOWN_CC ? '' : row.cc,
        cityName: row.cityName === UNKNOWN ? undefined : row.cityName,
        countryName: row.countryName === UNKNOWN ? undefined : row.countryName,
        location: row.location,
        postal: row.postal === UNKNOWN ? undefined : row.postal,
        region: row.region === UNKNOWN ? undefined : row.region,
        isp: row.isp,
        asnOrg: row.asnOrg,
    }))
}
